/**
 * Bounce Exit Intent Detection
 * Detects when user is about to leave the page and shows popup/redirects
 */

class BounceExitIntent {
  constructor() {
    this.popup = document.getElementById('bounceExitPopup');
    this.hasShown = false;
    this.sensitivity = 20; // pixels from top before triggering
    this.delay = 1000; // minimum time on page before exit intent can trigger (ms)
    this.pageLoadTime = Date.now();
    this.isCartAbandonment = false;

    // Get settings from data attributes
    // Note: popup element is required to get settings, even in redirect mode
    if (this.popup) {
      this.bounceUrl = this.popup.dataset.bounceUrl || '/pages/exclusive-offer';
      this.exitMode = this.popup.dataset.exitMode || 'popup'; // 'popup' or 'redirect'
      this.previewEnabled = this.popup.dataset.previewEnabled === 'true';
      this.cartAbandonmentEnabled = this.popup.dataset.cartAbandonment === 'true';
      this.isPreviewMode = this.checkPreviewMode();
      this.init();
    }
  }

  checkPreviewMode() {
    // Preview mode requires both backend setting enabled AND URL parameter
    if (!this.previewEnabled) {
      return false;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const previewParam = urlParams.get('preview');

    if (previewParam === 'true') {
      console.log('🔍 Preview mode activated for bounce offer');
      return true;
    }

    return false;
  }

  init() {
    // Prevent execution in Theme Editor to allow page editing
    if (window.Shopify && window.Shopify.designMode) {
      return;
    }

    // If preview is enabled in settings but main feature is disabled, check for preview param
    const bounceFeatureEnabled = this.popup?.dataset?.bounceFeatureEnabled === 'true';
    if (!bounceFeatureEnabled && this.previewEnabled && !this.isPreviewMode) {
      // Preview mode is enabled but no preview param, don't initialize
      return;
    }

    // Check if user has items in cart (only if cart abandonment is enabled)
    if (this.cartAbandonmentEnabled) {
      this.checkCartStatus();
    }

    // In preview mode, bypass the already-shown check
    if (!this.isPreviewMode) {
      // Check cookie to see if popup was already shown (persists for 7 days)
      if (this.getCookie('bounce_popup_shown') === 'true') {
        return;
      }
    }

    // Desktop exit intent detection
    document.addEventListener('mouseout', (e) => this.handleMouseOut(e));

    // Mobile - detect back button or tab close
    window.addEventListener('beforeunload', (e) => this.handleBeforeUnload(e));

    // Setup popup close handlers (only needed for popup mode)
    if (this.exitMode === 'popup') {
      this.setupCloseHandlers();
    }
  }

  checkCartStatus() {
    // Check if cart has items
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        this.isCartAbandonment = cart.item_count > 0;
      })
      .catch(err => { console.log('Cart check failed', err); });
  }

  handleMouseOut(e) {
    // Determine if we should trigger based on cart abandonment setting
    const shouldTrigger = !this.cartAbandonmentEnabled || this.isCartAbandonment;
    
    // Check if mouse is leaving from top of viewport
    if (e.clientY < this.sensitivity && !this.hasShown && shouldTrigger) {
      // Check if enough time has passed since page load
      if (Date.now() - this.pageLoadTime > this.delay) {
        if (this.exitMode === 'redirect') {
          this.redirectToBounce();
        } else {
          this.showPopup();
        }
      }
    }
  }

  handleBeforeUnload(e) {
    // Determine if we should trigger based on cart abandonment setting
    const shouldTrigger = !this.cartAbandonmentEnabled || this.isCartAbandonment;
    
    // Only trigger for mobile if conditions are met
    if (shouldTrigger && !this.hasShown) {
      // Note: Most modern browsers ignore custom messages in beforeunload
      // This is primarily for logging/tracking purposes
      this.hasShown = true;
      // Cookie will be set only when user clicks through to bounce page
    }
  }

  showPopup() {
    if (this.hasShown || !this.popup) return;

    this.hasShown = true;
    this.popup.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Cookie will be set only when user clicks through to bounce page
    // This allows users who dismiss the popup to see it again on future visits

    // Track event (if analytics available)
    this.trackEvent('exit_intent_shown');
  }

  async redirectToBounce() {
    if (this.hasShown) return;

    this.hasShown = true;

    // Mark as shown for 7 days (allows re-engagement after a week)
    this.setCookie('bounce_popup_shown', 'true', 7);

    // Grant bounce page access and track the event
    await this.grantBouncePageAccess();
    sessionStorage.setItem('bounceOfferClicked', 'true');

    // Track event (if analytics available)
    this.trackEvent('exit_intent_redirect');

    // Append preview parameter if in preview mode
    let redirectUrl = this.bounceUrl;
    if (this.isPreviewMode) {
      const url = new URL(redirectUrl, window.location.origin);
      url.searchParams.set('preview', 'true');
      redirectUrl = url.toString();
      console.log('🔍 Preview mode: Redirecting with preview parameter');
    }

    // Redirect to the bounce page
    window.location.href = redirectUrl;
  }

  closePopup() {
    if (!this.popup) return;

    this.popup.classList.remove('active');
    document.body.style.overflow = '';

    // Set cookie to remember user dismissed the popup (7 days)
    // Only set if not in preview mode, so preview can be tested multiple times
    if (!this.isPreviewMode) {
      this.setCookie('bounce_popup_shown', 'true', 7);
    }

    // Track event
    this.trackEvent('exit_intent_dismissed');
  }

  setupCloseHandlers() {
    if (!this.popup) return;

    // Close button
    const closeBtn = this.popup.querySelector('.bounce-exit-popup__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closePopup());
    }

    // Dismiss link
    const dismissLink = this.popup.querySelector('.bounce-exit-popup__dismiss');
    if (dismissLink) {
      dismissLink.addEventListener('click', () => this.closePopup());
    }

    // Click outside to close
    this.popup.addEventListener('click', (e) => {
      if (e.target === this.popup) {
        this.closePopup();
      }
    });

    // Track CTA click and append preview parameter if needed
    const ctaButton = this.popup.querySelector('.bounce-exit-popup__button');
    if (ctaButton) {
      // Append preview parameter to button link if in preview mode
      if (this.isPreviewMode) {
        const currentHref = ctaButton.getAttribute('href');
        if (currentHref) {
          const url = new URL(currentHref, window.location.origin);
          url.searchParams.set('preview', 'true');
          ctaButton.setAttribute('href', url.toString());
        }
      }

      ctaButton.addEventListener('click', () => {
        // Mark as shown for 7 days when user clicks through
        this.setCookie('bounce_popup_shown', 'true', 7);
        this.trackEvent('exit_intent_accepted');
        this.grantBouncePageAccess();
        sessionStorage.setItem('bounceOfferClicked', 'true');
      });
    }

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.classList.contains('active')) {
        this.closePopup();
      }
    });
  }

  async grantBouncePageAccess() {
    // Set bounce_page_access cookie with 1-week expiry timestamp as value
    const now = new Date();
    const accessExpiry = new Date(now.getTime() + (7 * 24 * 60 * 60 * 1000)); // 1 week
    const expiryTimestamp = accessExpiry.getTime(); // Store timestamp as cookie value

    // Grant 1-week page access - value is the expiry timestamp
    document.cookie = `bounce_page_access=${expiryTimestamp};expires=${accessExpiry.toUTCString()};path=/;SameSite=Lax`;

    // Generate CSRF token for API security
    const csrfToken = await this.generateCSRFToken();

    // Store CSRF token in cookie (10 min expiry for security)
    const csrfExpiry = new Date(now.getTime() + (10 * 60 * 1000)); // 10 minutes
    document.cookie = `bounce_csrf_token=${csrfToken};expires=${csrfExpiry.toUTCString()};path=/;SameSite=Lax`;

    // console.log(`Bounce page access granted until ${accessExpiry.toISOString()}`);
    // console.log(`CSRF token generated: ${csrfToken}`);

    // Register CSRF token with Worker
    await this.registerCSRFToken(csrfToken);

    // Note: bounce_offer_expiry will be set after coupon generation
  }

  async generateCSRFToken() {
    // Generate cryptographically secure random token
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  async registerCSRFToken(token) {
    // Register token with Worker (it will store in KV with TTL)
    try {
      const workerUrl = 'https://pe-exit-intent-discount.mybeta.workers.dev';
      const response = await fetch(`${workerUrl}/register-csrf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: token,
          timestamp: Date.now()
        })
      });

      if (response.ok) {
        // console.log('CSRF token registered successfully');
      } else {
        // console.warn('Failed to register CSRF token');
      }
    } catch (error) {
      // console.warn('Could not register CSRF token:', error);
    }
  }

  trackEvent(eventName) {
    // Gather user and cart data for tracking
    const eventData = this.getEventData(eventName);

    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, {
        'event_category': 'bounce_page',
        'event_label': this.isCartAbandonment ? 'cart_abandonment' : 'exit_intent',
        'cart_value': eventData.cartValue,
        'cart_items': eventData.cartItemCount
      });
    }

    // Klaviyo tracking (if available)
    if (typeof _learnq !== 'undefined') {
      _learnq.push(['track', eventName, eventData]);

      // Identify user if email is available
      if (eventData.email) {
        _learnq.push(['identify', {
          '$email': eventData.email,
          '$first_name': eventData.firstName,
          '$last_name': eventData.lastName,
          'BounceOfferShown': true,
          'LastBounceOfferDate': new Date().toISOString()
        }]);
      }
    }

    // Facebook Pixel (if available)
    if (typeof fbq !== 'undefined') {
      fbq('trackCustom', eventName, {
        cart_abandonment: this.isCartAbandonment,
        value: eventData.cartValue,
        currency: eventData.currency
      });
    }

    // console.log('Bounce event tracked:', eventName, eventData);
  }

  getEventData(eventName) {
    const data = {
      event: eventName,
      cart_abandonment: this.isCartAbandonment,
      bounce_url: this.bounceUrl,
      page_url: window.location.href,
      timestamp: new Date().toISOString(),
      cartValue: 0,
      cartItemCount: 0,
      currency: 'EUR',
      cartItems: []
    };

    // Try to get cart data from global Shopify object
    if (typeof Shopify !== 'undefined' && Shopify.cart) {
      data.cartValue = (Shopify.cart.total_price / 100).toFixed(2);
      data.cartItemCount = Shopify.cart.item_count;
      data.currency = Shopify.currency.active;

      if (Shopify.cart.items) {
        data.cartItems = Shopify.cart.items.map(item => ({
          ProductID: item.product_id,
          ProductName: item.product_title,
          Quantity: item.quantity,
          Price: (item.price / 100).toFixed(2),
          ItemURL: window.location.origin + '/products/' + item.handle
        }));
      }
    }

    // Try to get customer data if logged in
    if (typeof Shopify !== 'undefined' && Shopify.customer) {
      data.email = Shopify.customer.email;
      data.firstName = Shopify.customer.first_name;
      data.lastName = Shopify.customer.last_name;
      data.customerId = Shopify.customer.id;
    }

    return data;
  }

  // Cookie management helpers
  setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  }

  getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
    }
    return null;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.bounceExitIntent = new BounceExitIntent();
  });
} else {
  window.bounceExitIntent = new BounceExitIntent();
}

// Helper function to reset bounce exit intent (for testing)
// Call this in browser console: resetBounceExitIntent()
window.resetBounceExitIntent = function() {
  document.cookie = 'bounce_popup_shown=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  console.log('Bounce exit intent cookie cleared. Reload the page to test again.');
};
